"use client"

import { useState, useCallback, useRef } from "react"

const MAX_ATTEMPTS = 5
const LOCKOUT_DURATION_MS = 2 * 60 * 1000

interface LoginRateLimit {
  failedAttempts: number
  isLockedOut: boolean
  lockoutRemainingSeconds: number
  recordFailure: () => void
  reset: () => void
  shouldSuggestForgotPassword: boolean
}

/**
 * Client-side rate limiting for login attempts.
 * After MAX_ATTEMPTS consecutive failures, locks the form for LOCKOUT_DURATION_MS.
 * Suggests "Forgot password?" after 3 failures.
 */
export function useLoginRateLimit(): LoginRateLimit {
  const [failedAttempts, setFailedAttempts] = useState(0)
  const [lockoutUntil, setLockoutUntil] = useState<number | null>(null)
  const [lockoutRemainingSeconds, setLockoutRemainingSeconds] = useState(0)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const startLockoutTimer = useCallback((until: number) => {
    if (timerRef.current) clearInterval(timerRef.current)
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((until - Date.now()) / 1000))
      setLockoutRemainingSeconds(remaining)
      if (remaining <= 0) {
        if (timerRef.current) clearInterval(timerRef.current)
        setLockoutUntil(null)
        setFailedAttempts(0)
      }
    }
    tick()
    timerRef.current = setInterval(tick, 1000)
  }, [])

  const recordFailure = useCallback(() => {
    setFailedAttempts((prev) => {
      const next = prev + 1
      if (next >= MAX_ATTEMPTS) {
        const until = Date.now() + LOCKOUT_DURATION_MS
        setLockoutUntil(until)
        startLockoutTimer(until)
      }
      return next
    })
  }, [startLockoutTimer])

  const reset = useCallback(() => {
    setFailedAttempts(0)
    setLockoutUntil(null)
    setLockoutRemainingSeconds(0)
    if (timerRef.current) clearInterval(timerRef.current)
  }, [])

  const isLockedOut = lockoutUntil !== null && Date.now() < lockoutUntil

  return {
    failedAttempts,
    isLockedOut,
    lockoutRemainingSeconds,
    recordFailure,
    reset,
    shouldSuggestForgotPassword: failedAttempts >= 3,
  }
}
